import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../../../../layouts/Layout";
import * as PR from "../../../../prime-react/index";
import "./EditReport.scss";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  getSports,
  getReportById,
  getFileDetails,
  getGroupsWithPermission,
  getAlertDetails
} from "../../../../services/api";
import { fileUploadLimit, getPlainText, maxNumberOfFiles, permission,reportsFileTypesConfig,roleIds, typeFilter, textSanitize, editorConfig, validationMessages, usLocationIds, trimFileName } from "../../../../utils/reuse";
import { useToast, ErrorMessage } from "../../../../context/ToastContext";
import DOMPurify from "dompurify";
import { inputSearchHandler } from "../../../../utils/inputValidate";

const EditReport = () => {
  const { showToast, clearError } = useToast();
  const [reportDetail, setReportDetail] = useState({});
  const [initialRegulators, setInitialRegulators] = useState([]);
  const [initialUsRegulators, setInitialUsRegulators] = useState([]);
  const [initialGoverningBodies, setInitialGoverningBodies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [deletedAttachments, setDeletedAttachments] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { search, state, pathname } = useLocation();
  const fileUploadRef = useRef(null);
  const queryParams = new URLSearchParams(search);
  const id = queryParams.get("id");
  const sessionData = useSelector((state) => state.auth.sessionData);
  const headers = useMemo(() => {
    return {
      sessionid: sessionData.sessionId,
      "Content-Type": "multipart/form-data",
    };
  }, [sessionData.sessionId]);

  const [regulators, setRegulators] = useState([]);
  const [usRegulators, setusRegulators] = useState([]);
  const [governingBodies, setGoverningBodies] = useState([]);
  const [sports, setSports] = useState([]);
  const [alertDetails, setAlertDetails] = useState(null);
  const [alertIdSearch, setAlertIdSearch] = useState(null);
  const [error, setError] = useState(null)
  const [alertIdloading, setAlertIdloading] = useState(false);
  const [isEditorReady, setIsEditorReady] = useState(false);

  useEffect(() => {
      window.scrollTo(0, 0);
  }, [pathname]);

  const validationSchema = Yup.object()
  .shape({
    title: Yup.string().required("Report title is required"),
    attachments: Yup.array()
      .max(maxNumberOfFiles, validationMessages.maxNumberOfFiles)
      .test('fileType', validationMessages.allFileTypesWithoutImage, (files) => {
        if (!files) return true;
        return files?.every((file) => reportsFileTypesConfig.includes(file.type))
      })
      .test('fileSize', validationMessages.sizeExceed, (files) => {
        if (!files) return true;
        return files?.every((file) => {
          return file.size <= fileUploadLimit.value
        })
      }),
    reportDescription: Yup.string().test( "valid-text", "Enter valid text", (value) =>  !(getPlainText(value) === 0 && value !== null))
          .required("Description is required"),
    reportType: Yup.object().nullable().required("Report type is required"),
    sport: Yup.string().when('reportType', {
      is: (reportType) => {
        return reportType?.name?.toLowerCase() !== "summary";
      },
      then: () => Yup.string().nullable().required("Sport is required"),
      otherwise: () => Yup.string().nullable(),
    }),
    alertID: Yup.object().when('reportType', {
      is: (reportType) => {
        return reportType?.name?.toLowerCase() === "suspicious betting";
      },
      then: () => Yup.string().nullable().required("Alert ID is required"),
      otherwise: () => Yup.string().nullable(),
    }),
    regulators: Yup.array().of(Yup.object().nullable()),
    usRegulators: Yup.array().of(Yup.object().nullable()),
    governingBodies: Yup.array().of(Yup.object().nullable()),
  })
  .test(
    "totalFileLimit",
    validationMessages.maxNumberOfFiles,
    function (values) {
      const totalFiles = values.attachments.length + attachments.length;
      return totalFiles <= maxNumberOfFiles;
    }
  )
  .test(
    "oneOfRequired",
    "At least one US regulator, world regulator, or governing body is required",
     function ({ regulators = [], governingBodies = [], usRegulators = [] }) {
        return regulators?.length > 0 || governingBodies?.length > 0 || usRegulators?.length > 0;
      }
  );


  const [initialValues, setInitialValues] = useState({
    title: "",
    attachments: [],
    reportDescription: "",
    reportType: null,
    sport: null,
    alertID: "",
    regulators: [],
    usRegulators: [],
    governingBodies: [],
    enableDownload: false
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    enableReinitialize:true,
    onSubmit: (values) => {
      const totalFiles = values.attachments.length + attachments.length;
      if (totalFiles > maxNumberOfFiles) {
        formik.setErrors({
          ...formik.errors,
          attachments: validationMessages.maxNumberOfFiles,
        });
        return;
      }
      if (values.regulators?.length === 0 && values.governingBodies?.length === 0 &&  values.usRegulators?.length === 0) {
        formik.setErrors({
          ...formik.errors,
          oneOfRequired: "At least one US regulator, world regulator, or governing body is required",
        });
        return;
      }
      const newRegulators = values.regulators.filter(
        (reg) => !initialRegulators.some((initial) => initial.id === reg.id)
      );
      const newUsRegulators = values.usRegulators.filter(
        (reg) => !initialUsRegulators.some((initial) => initial.id === reg.id)
      );
      const newGoverningBodies = values.governingBodies.filter(
        (gb) => !initialGoverningBodies.some((initial) => initial.id === gb.id)
      );
      let data = {
        title: values.title,
        description: textSanitize(values.reportDescription),
        reportID: parseInt(id),
        reportType: values.reportType.name.toLowerCase(),
        deletedAttachments, 
      };

      if (newRegulators.length > 0 || newGoverningBodies.length > 0 || newUsRegulators?.length > 0) {
        data.receivers = [
          ...newRegulators?.map((reg) => reg.id),
          ...newGoverningBodies?.map((gb) => gb.id),
          ...newUsRegulators?.map((gb) => gb.id),
        ];
      }
      let sportName = ''
      if (data?.reportType !== 'summary') {
        data.sportID = values.sport;
        const selectedSport = sports.find(sport => sport.value === values.sport);
        sportName = selectedSport ? selectedSport.label : "";
      }
      if (data?.reportType === 'suspicious betting') {
        data.alertID = alertDetails?.legacyID;
      }
      if (values.enableDownload !== initialValues.enableDownload) {
        data.enableDownload = values.enableDownload;
      }
      navigate("/preview-report" , {state: {formvalues: values, reportData: data, attachments, uploadAttachements: values.attachments , sportName, initialRegulators, initialUsRegulators, initialGoverningBodies, deletedAttachments, queryParams: { id} }});
    },
  });

  const handleFileSelect = (event) => {

    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }
    const currentAttachments = formik.values.attachments || [];
    const newAttachment = event.files[0];
    const duplicateFiles = (
      currentAttachments.some(existingFile => existingFile.name === newAttachment.name) ||
      attachments.some(existingFile => existingFile.name === newAttachment.name)
    )  
    if (duplicateFiles) {
      showToast("warn", validationMessages.uniqueFileNames);
      fileUploadRef.current.clear();
    } else if (currentAttachments?.length + 1 + attachments?.length > maxNumberOfFiles) {
      showToast("warn", validationMessages.maxNumberOfFiles);
      fileUploadRef.current.clear();
    } else {
      const updatedAttachments = [...currentAttachments, newAttachment];
      formik.setFieldValue("attachments", updatedAttachments);
    }
  };
  
  const handleDeleteFile = (index) => {
    const updatedAttachments = [...formik.values.attachments];
    updatedAttachments.splice(index, 1);
    formik.setFieldValue("attachments", updatedAttachments);
  };

  useEffect(() => {
    if(!state?.formvalues){
    getReportById(id, headers, dispatch, (response) => {
      if (response.result === "SUCCESS") {
        const responseData = response.data;
        if(responseData?.length){
          setReportDetail(responseData[0])
        } else {
          navigate('/reports');
        }        
        setLoading(false);
      } else {
        navigate('/reports');
      }
    });
  } else{
    setInitialGoverningBodies(state?.initialGoverningBodies);
    setInitialRegulators(state?.initialRegulators);
    setInitialUsRegulators(state?.initialUsRegulators);
    setAttachments(state?.attachments);
    setDeletedAttachments(state?.deletedAttachments);
    setLoading(false)
  }
  }, [clearError, dispatch, headers, id, navigate, showToast, state?.attachments, state?.deletedAttachments, state?.formvalues, state?.initialGoverningBodies, state?.initialRegulators, state?.initialUsRegulators]);

  useEffect(() => {
    if (reportDetail?.reportID && isEditorReady) {
      const sportId =
        sports?.find((sport) => sport.label === reportDetail.sportName)
          ?.value || "";
      const reportType =
        typeFilter?.find((type) => type.name === reportDetail.reportType) ||
        null;
      const governingBodyOptions = governingBodies.map(g => g.id);
      const regulatorOptions = regulators.map(r => r.id);
      const usRegulatorsOptions = usRegulators.map(r => r.id);
      const savedGoverningBody = reportDetail.receivers?.filter(receiver => receiver.role === "governingbody" &&
        governingBodyOptions.includes(receiver.id));
      const savedRegulators = reportDetail.receivers?.filter(receiver => receiver.role === "regulator" &&
        regulatorOptions.includes(receiver.id));
      const savedUsRegulators = reportDetail.receivers?.filter(receiver => receiver.role === "regulator" &&
        usRegulatorsOptions.includes(receiver.id));
      setInitialGoverningBodies(savedGoverningBody);
      setInitialRegulators(savedRegulators);
      setInitialUsRegulators(savedUsRegulators);
      setInitialValues({
        title: reportDetail.title || "",
        reportDescription: reportDetail?.description ? DOMPurify.sanitize(reportDetail?.description) : '',
        reportType: reportType,
        sport: sportId,
        alertID: reportDetail?.alertID ? reportDetail.alertID : '',
        regulators: savedRegulators,
        usRegulators: savedUsRegulators,
        governingBodies: savedGoverningBody,
        attachments: [],
        enableDownload: reportDetail?.enableDownload ? reportDetail?.enableDownload : false
      });

      if (reportDetail.fileAttachment) {
        const attachmentsArray = JSON.parse(reportDetail.fileAttachment);
        if (attachmentsArray && attachmentsArray.length > 0) {
          const attachmentPromises = attachmentsArray?.map((attachment) =>
            getFileDetails(attachment.key, headers, dispatch)
          );
          Promise.all(attachmentPromises).then((attachmentResponses) => {
            const attachmentData = attachmentResponses?.map(
              (attachmentResponse, index) => ({
                name: attachmentsArray?.length > 0 && attachmentsArray[index].name,
                data: attachmentResponse?.data,
                key: attachmentsArray?.length > 0 && attachmentsArray[index].key,
              })
            );
            setAttachments(attachmentData);
          });
        }
      }

    }
  }, [dispatch, governingBodies, headers, isEditorReady, regulators, reportDetail, sports, usRegulators])
  useEffect(() => {
    if(state?.formvalues){
      setInitialValues(state?.formvalues)
    }
  }, [state?.formvalues])

  useEffect(() => {
    getGroupsWithPermission(roleIds.regulator, permission?.reports,  headers, dispatch, (response) => {
      if (response.result === "SUCCESS") {
        const responseData = response.data;
        const result = responseData?.length > 0 ? responseData : [];
        const regulators = result.filter(r => !usLocationIds.includes(r.locationID));
        const usRegulators = result.filter(r => usLocationIds.includes(r.locationID));
        setRegulators(regulators);
        setusRegulators(usRegulators);
      } else {
        const error = response.error;
        showToast(error.severity, error?.errorMsg || error?.summary);
      }
    });

    getGroupsWithPermission(roleIds?.governingbody,permission?.reports, headers, dispatch, (response) => {
      if (response.result === "SUCCESS") {
        const responseData = response.data;
        const result = responseData?.length > 0 ? responseData : [];
        setGoverningBodies(result);
      } else {
        const error = response.error;
        showToast(error.severity, error?.errorMsg || error?.summary);
      }
    });

    getSports(null, headers, dispatch, (response) => {
      if (response.result === "SUCCESS") {
        const sports = response.data?.map((sport) => ({
          label: sport.name,
          value: sport.id,
        }));
        setSports(sports?.length > 0 ? sports?.sort((a, b) => a.label.localeCompare(b.label)) : []);
        if (sports.length === 0) {
          showToast("warn", "No records found");
        }
      } else {
        setSports([]);
        const error = response.error;
        showToast(error.severity, error.errorMsg || error.summary);
      }
    });
  }, [dispatch, headers, showToast]);

  const disableSelectedOptions = (options, selectedValues) => {
    return options?.map((option) => ({
      ...option,
      disabled: selectedValues.includes(option.value),
    }))?.sort((a, b) => a.label.localeCompare(b.label));
  };
  /* const downloadAttachment = (fileData, fileName) => {
    const link = document.createElement("a");
    link.href = fileData;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }; */
  const handleDeleteExistingFile = (index) => {
    const updatedAttachments = [...attachments];
    const deletedAttachment = updatedAttachments.splice(index, 1)[0];
    setAttachments(updatedAttachments);
    setDeletedAttachments([
      ...deletedAttachments,
      {
        type: "application/pdf", 
        key: deletedAttachment.key, 
      },
    ]);
  };

  const getSearchedAlertDetails = useCallback((legacyId) => {
    getAlertDetails(legacyId, headers, dispatch, ({ result, error, data }) => {
      if (result === 'SUCCESS' && (data && data?.legacyID)) {
        setAlertDetails(data);
        setAlertIdSearch(data.legacyID);
        setError(null);
      } else {
        setError("Alert ID not found");
        setAlertDetails(null);
        setTimeout(() => {
          setError(null);
        }, 2500);
      }
    setAlertIdloading(false);
    }, { isTitle: true });
  }, [dispatch, headers]);

  const searchHandler = e => {
    e?.preventDefault();
    const normalizedSearchId = alertIdSearch?.toString().toLowerCase().trim();
    if (!normalizedSearchId || normalizedSearchId === 'alert' || normalizedSearchId === 'alert-') {
      setError("Invalid Alert ID");
      setAlertDetails(null);
      return;
    } else {
      setAlertIdloading(true);
      let legacyId = alertIdSearch;
      if (!alertIdSearch.toLowerCase().startsWith('alert-')) {
        legacyId = `alert-${alertIdSearch}`
      }
      getSearchedAlertDetails(legacyId);
    }
  }

  useEffect(() => {
    if (
      initialValues?.reportType?.name.toLowerCase() === "suspicious betting" &&
      initialValues?.alertID
    ) {
      const legacyId = initialValues?.alertID?.toLowerCase().startsWith("alert-")
        ? initialValues?.alertID
        : `alert-${initialValues?.alertID}`;

      setAlertIdloading(true);
      getSearchedAlertDetails(legacyId);
    }
  }, [getSearchedAlertDetails, initialValues]);

  const isSuspiciousBetting = formik.values?.reportType?.name?.toLowerCase() === 'suspicious betting';
  const isAlertNameMissingOrError = !alertDetails?.alertName || error;
  const isFormUnchanged = !formik.dirty && !deletedAttachments.length;

  const shouldDisable = state?.formvalues
    ? (isSuspiciousBetting && isAlertNameMissingOrError)
    : (isFormUnchanged || (isSuspiciousBetting && isAlertNameMissingOrError));
  // Trigger once editor mounts
  const handleEditorInit = () => {
    setIsEditorReady(true);
  };

  return (
    <Layout backBtn={"reports"} backBtnLabel="Reports">
      <section className="news-tab news-details-section general-news-section">
        { loading && 
        <div className="loading">
          <i className="pi pi-spin pi-spinner"></i>
        </div> }
        <div className="flex align-items-center justify-content-between mb-4">
          <div className="left">
            <h1>Edit Report</h1>
          </div>
          <div>
          <ErrorMessage id={`report_api_error`} />
          </div>
          <div className="right">
            <PR.Button
              label="Update & Preview"
              className="action-buttons save-button"
              disabled={shouldDisable}
              onClick={formik.handleSubmit}
              type="submit"
            />
          </div>
        </div>
        <form autoComplete="off" onSubmit={formik.handleSubmit}>
          <div className="grid">
            <div className="col-12">
              <div className="new-list-section">
                <div className="list-wrapper">
                  <div className="edit-details">
                    <div className="card">
                      <div className="col-12 p-0 mb-4">
                        <div className="flex flex-column form-input gap-2">
                          <label htmlFor="title">
                            Report Title <span className="text-red">*</span>
                          </label>
                          <PR.InputText
                            id="title"
                            name="title"
                            type="text"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={`input-field ${
                              formik.touched.title && formik.errors.title
                                ? "p-invalid"
                                : ""
                            }`}
                          />
                          {formik.errors.title && formik.touched.title && (
                            <div className="error-message">
                              {formik.errors.title}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-12 p-0">
                        <div className="flex flex-column gap-2">
                          <span className="label">Attach Files</span>
                          <div className="card create-file-upload flex align-items-center">
                            <PR.FileUpload
                              auto
                              mode="advanced"
                              name="attachments"
                              accept={reportsFileTypesConfig?.toString()}
                              chooseLabel="Choose File"
                              multiple
                              ref={fileUploadRef}
                              onSelect={handleFileSelect}
                              onFocus={() => formik.setFieldTouched("attachments")}
                              customUpload
                            />
                            <span className="ml-2">
                              {validationMessages.maxFiles}
                            </span>
                          </div>
                          {formik.values.attachments && (
                            <div className="uploaded-file-container flex flex-column gap-2">
                              {formik.values.attachments.map((attachment, index) => (
                                <div className="uploaded-files" key={index}>
                                  {trimFileName(attachment.name)}
                                  <i
                                    className="pi pi-trash"
                                    onClick={() => handleDeleteFile(index)}
                                    style={{ cursor: 'pointer', marginLeft: '10px' }}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                          {formik.errors.attachments &&
                            (formik.touched.attachments || formik.submitCount > 0) && (
                              <div className="error-message" style={{ color: "red" }}>
                                {Array.isArray(formik.errors.attachments) ? (
                                  Array.from(new Set(formik.errors.attachments))?.map(
                                    (error, index) => (
                                      <div key={index}>{error}</div>
                                    )
                                  )
                                ) : (
                                  <div>{formik.errors.attachments}</div>
                                )}
                              </div>
                            )}
                        </div>
                      </div>
                      {attachments.length > 0 && (
                        <div className="col-12 p-0 mt-2">
                          <div className="flex align-items-center gap-2 flex-wrap">
                            {attachments?.map((attachment, index) => (
                              <div
                                key={index}
                                className="attachment-item flex align-items-center"
                              >
                                <PR.Button
                                  type="button"
                                  icon="pi pi-book"
                                  label={trimFileName(attachment.name)}
                                  className="attachment-button"
                              /*     onClick={() =>
                                    downloadAttachment(
                                      attachment.data,
                                      attachment.name
                                    )
                                  } */
                                />
                                <i
                                  className="pi pi-trash delete-attachment-icon"
                                  onClick={() => handleDeleteExistingFile(index)}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="col-12 p-0 mt-4">
                        <div className="flex flex-column gap-2">
                          <span className="label">
                            Report Description{" "}
                            <span className="text-red">*</span>
                          </span>
                          <div className="card content-editor">
                            <PR.Editor
                             id="reportDescription"
                             key={'report-description'}
                              value={formik.values.reportDescription}
                              name="reportDescription"
                              onTextChange={(e) => {
                              formik.setFieldValue('reportDescription',e.htmlValue);
                              }}
                              modules={editorConfig.modules}
                              formats={editorConfig.formats}
                              onLoad={handleEditorInit}
                              style={{ height: "450px" }}
                            />
                          </div>
                          {formik.errors.reportDescription && (
                            <div className="error-message">
                              {formik.errors.reportDescription}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="grid mt-4">
                        <div className="col-6">
                          <div className="flex flex-column form-input gap-2">
                            <label htmlFor="reportType">
                              Report Type
                            </label>
                            <PR.Dropdown
                              inputId="reportType"
                              value={formik.values.reportType}
                              onChange={(e) => {
                                formik.setFieldValue("reportType", e.value)
                                formik.setFieldValue("alertID", "");
                                setAlertIdSearch("");
                              }}
                              options={typeFilter}
                              optionLabel="name"
                              name="reportType"
                              placeholder="Select Report type"
                              className={`w-full ${
                                formik.errors.reportType &&
                                formik.touched.reportType
                                  ? "is-invalid"
                                  : ""
                              }`}
                              disabled
                            />
                            {formik.errors.reportType &&
                              formik.touched.reportType && (
                                <div className="error-message">
                                  {formik.errors.reportType}
                                </div>
                              )}
                          </div>
                        </div>
                        <div className="col-6">
                          {formik.values?.reportType?.name?.toLowerCase() !== 'summary' &&
                            <div className="flex flex-column form-input gap-2">
                              <label htmlFor="sport">
                                Sport <span className="text-red">*</span>
                              </label>
                              <PR.Dropdown
                                inputId="sport"
                                value={formik.values.sport}
                                onChange={(e) =>
                                  formik.setFieldValue("sport", e.value)
                                }
                                filter
                                resetFilterOnHide
                                name="sport"
                                options={sports}
                                optionLabel="label"
                                placeholder="Select sport"
                                className="w-full"
                              />
                              {formik.errors.sport && formik.touched.sport && (
                                <div className="error-message">
                                  {formik.errors.sport}
                                </div>
                              )}
                            </div>
                          }
                        </div>
                      </div>
                      <div className="grid mt-0 align-items-center">
                        <div className="col-6">
                          {formik.values?.reportType?.name?.toLowerCase() === 'suspicious betting' &&
                            <div className="flex flex-column form-input gap-2">
                              <label htmlFor="sport">
                                Alert ID <span className="text-red">*</span>
                              </label>
                              <span className="p-input-icon-left w-12">
                                {alertIdloading
                                  ? <i className="pi pi-spin pi-spinner" />
                                  : <i className="pi pi-search"></i>
                                }
                                <PR.InputText
                                  id="alertID"
                                  name="alertID"
                                  placeholder="Enter Alert ID"
                                  className={`search-box alertID ${formik.errors.alertID && formik.touched.alertID ? "is-invalid" : ""}`}
                                  value={formik.values.alertID}
                                  autoComplete="off"
                                  onChange={(e) => {
                                    setAlertDetails(null);
                                    const isValid = inputSearchHandler(e, setAlertIdSearch);
                                    if (isValid) {
                                      formik.setFieldValue("alertID", e.target.value);
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      const isValid = inputSearchHandler(e, setAlertIdSearch);
                                      if (isValid) {
                                        searchHandler(e);
                                      }
                                    }
                                  }}
                                />
                                {error && <p className="error-message mt-1 absolute">{error}</p>}
                              </span>

                              {!alertIdSearch && formik.touched.alertID && formik.errors.alertID && (
                                <div className="error-message">{formik.errors.alertID}</div>
                              )}
                            </div>
                          }
                        </div>
                        <div className="col-6 form-input">
                              {(alertIdSearch && alertDetails?.alertName) && (
                                <div className="alertId-msg-display">
                                  <i className="pi pi-check-circle"></i> {alertDetails?.alertName}
                                </div>
                              )}
                        </div>
                      </div>
                      <div className="grid mt-4">
                        <div className="col-12 pt-0 pb-0">
                          <label htmlFor="regulators">
                            Publish and Send by email to{" "}
                            <span className="text-red">*</span>
                          </label>
                        </div>
                        <div className="col-4">
                          <div className="flex flex-column form-input gap-2">
                            <label htmlFor="usRegulators">
                              US Regulators 
                            </label>
                            <PR.MultiSelect
                            inputId="usRegulators"
                              value={formik.values.usRegulators?.map(
                                (reg) => reg.id
                              )}
                              onChange={(e) => {
                                formik.setFieldValue(
                                  "usRegulators",
                                  e.value?.map((id) =>{
                                    const v = usRegulators.find((reg) => reg.id === id)
                                    return {id: v.id, name:v.name}
                                  }
                                  )
                                );
                              }}
                              options={disableSelectedOptions(
                                usRegulators?.map((reg) => ({
                                  label: reg.name,
                                  value: reg.id,
                                })),
                                initialUsRegulators?.map((reg) => reg.id)
                              )}
                              optionLabel="label"
                              filter
                              resetFilterOnHide
                              placeholder="Select"
                              maxSelectedLabels={3}
                              className="w-full"
                              showSelectAll
                              pt={{
                                  checkboxContainer: {
                                      onClick: (e) => {
                                          e.stopPropagation();
                                          const parent = e.target?.parentNode;
                                          if (parent && typeof parent.click === 'function') {
                                              parent.click();
                                          }
                                      },
                                  },
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="flex flex-column form-input gap-2">
                             <label htmlFor="regulators">
                              World Regulators
                            </label>
                            <PR.MultiSelect
                            inputId="regulators"
                              value={formik.values.regulators?.map(
                                (reg) => reg.id
                              )}
                              onChange={(e) => {
                                formik.setFieldValue(
                                  "regulators",
                                  e.value?.map((id) =>{
                                    const v = regulators.find((reg) => reg.id === id)
                                    return {id: v.id, name:v.name}
                                  }
                                  )
                                );
                              }}
                              options={disableSelectedOptions(
                                regulators?.map((reg) => ({
                                  label: reg.name,
                                  value: reg.id,
                                })),
                                initialRegulators?.map((reg) => reg.id)
                              )}
                              optionLabel="label"
                              filter
                              resetFilterOnHide
                              placeholder="Select"
                              maxSelectedLabels={3}
                              className="w-full"
                              showSelectAll
                              pt={{
                                  checkboxContainer: {
                                      onClick: (e) => {
                                          e.stopPropagation();
                                          const parent = e.target?.parentNode;
                                          if (parent && typeof parent.click === 'function') {
                                              parent.click();
                                          }
                                      },
                                  },
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="flex flex-column form-input gap-2">
                            <label htmlFor="governingBodies">
                             Sport Governing Bodies
                            </label>
                            <PR.MultiSelect
                              inputId="governingBodies"
                              value={formik.values.governingBodies?.map(
                                (body) => body.id
                              )}
                              onChange={(e) => {
                                formik.setFieldValue(
                                  "governingBodies",
                                  e.value?.map((id) =>
                                    {
                                      const v = governingBodies.find((body) => body.id === id);
                                      return {id: v.id, name:v.name}
                                    }
                                  )
                                );
                              }}
                              options={disableSelectedOptions(
                                governingBodies?.map((body) => ({
                                  label: body.name,
                                  value: body.id,
                                })),
                                initialGoverningBodies?.map((body) => body.id)
                              )}
                              optionLabel="label"
                              filter
                              resetFilterOnHide
                              placeholder="Select"
                              maxSelectedLabels={3}
                              className="w-full"
                              showSelectAll
                              pt={{
                                  checkboxContainer: {
                                      onClick: (e) => {
                                          e.stopPropagation();
                                          const parent = e.target?.parentNode;
                                          if (parent && typeof parent.click === 'function') {
                                              parent.click();
                                          }
                                      },
                                  },
                              }}
                            />
                          </div>
                        </div>
                        {formik.errors.oneOfRequired &&
                          (formik.touched.regulators ||
                            formik.touched.governingBodies) && (
                            <div className="col-12 error-message">
                              {formik.errors.oneOfRequired}
                            </div>
                          )}
                      </div>

                      {(formik.values.usRegulators?.length > 0) && <div className="all-users-section mt-4">
                        <div className="switch-button flex gap-2 align-items-center">
                          <PR.InputSwitch id="enableDownload" name="enableDownload" checked={formik.values.enableDownload} onChange={(e) => formik.setFieldValue("enableDownload", e.value)} />
                          <label htmlFor="enableDownload">Enable Report Download</label>
                        </div>
                      </div>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="text-right mt-4">
            <PR.Button
              label="Update & Preview"
              className="action-buttons save-button"
              disabled={shouldDisable}
              type="submit"
            />
          </div>
        </form>
      </section>
    </Layout>
  );
};

export default EditReport;
